import { useState, useEffect, forwardRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { DrinkButton } from '@/components/DrinkButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database } from '@/integrations/supabase/types';
import { InviteModal } from '@/components/InviteModal';
import { DrinkGuidanceTooltip } from '@/components/DrinkGuidanceTooltip';
import { DrinkTrialModal } from '@/components/DrinkTrialModal';
import { DrinkInfoModal } from '@/components/DrinkInfoModal';
import { useDrinkGuidance } from '@/hooks/useDrinkGuidance';
import { useInviteToken } from '@/hooks/useInviteToken';
import { calculateDaysRemaining } from '@/lib/utils';
import { Info } from 'lucide-react';

type DrinkType = Database['public']['Enums']['drink_type'];

interface DrinkLogSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const DrinkLogSection = forwardRef<HTMLElement, DrinkLogSectionProps>(
  function DrinkLogSection({ onModalOpen, onModalClose }, ref) {
    const { user, profile, group } = useAuth();
    const { t } = useLanguage();
    const queryClient = useQueryClient();
    const [loggingDrink, setLoggingDrink] = useState<DrinkType | null>(null);
    const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showTrialModal, setShowTrialModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const { token: inviteToken } = useInviteToken();

    // Fetch drink logs for current user
    const { data: drinkLogs } = useQuery({
      queryKey: ['drink-logs', user?.id, group?.id],
      queryFn: async () => {
        if (!user || !group) return [];
        const { data } = await supabase
          .from('drink_logs')
          .select('*')
          .eq('user_id', user.id)
          .eq('group_id', group.id)
          .order('logged_at', { ascending: false });
        return data || [];
      },
      enabled: !!user && !!group
    });

    // Calculate stats
    const totalDrinks = drinkLogs?.length || 0;
    const remainingDrinks = Math.max(0, 100 - totalDrinks);
    const hasReachedLimit = totalDrinks >= 100;

    // Guidance hook
    const {
      showTooltip,
      isTrialTap,
      isReadyForFirstDrink,
      currentStep,
      handleTrialComplete,
      handleFirstDrinkLogged,
    } = useDrinkGuidance(totalDrinks);

    // Auto-hide confirmation message after delay
    useEffect(() => {
      if (confirmationMessage) {
        const timer = setTimeout(() => {
          setConfirmationMessage(null);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }, [confirmationMessage]);

    // Notify parent of modal state
    useEffect(() => {
      if (showInviteModal || showTrialModal || showInfoModal) {
        onModalOpen?.();
      } else {
        onModalClose?.();
      }
    }, [showInviteModal, showTrialModal, showInfoModal, onModalOpen, onModalClose]);

    const logDrinkMutation = useMutation({
      mutationFn: async (drinkType: DrinkType) => {
        if (!user || !group) throw new Error('Not authenticated');
        const { error } = await supabase.from('drink_logs').insert({
          user_id: user.id,
          group_id: group.id,
          drink_type: drinkType
        });
        if (error) throw error;
      },
      onSuccess: (_, drinkType) => {
        // Invalidate ALL drink-related queries to sync all views
        queryClient.invalidateQueries({ queryKey: ['drink-logs'] });
        queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
        queryClient.invalidateQueries({ queryKey: ['challenge-drink-logs'] });
        queryClient.invalidateQueries({ queryKey: ['all-drink-logs'] });
        queryClient.invalidateQueries({ queryKey: ['user-streak'] });
        
        const messageKey = drinkType === 'beer' ? 'dashboardBeerLogged' 
          : drinkType === 'wine' ? 'dashboardWineLogged' 
          : 'dashboardShotLogged';
        setConfirmationMessage(t(messageKey));
        setLoggingDrink(null);
        
        // Mark first drink logged in guidance
        if (isReadyForFirstDrink) {
          handleFirstDrinkLogged();
        }
      },
      onError: () => {
        setConfirmationMessage(null);
        setLoggingDrink(null);
      }
    });

    const handleLogDrink = (drinkType: DrinkType) => {
      // If this is the trial tap, show trial modal instead of logging
      if (isTrialTap) {
        setShowTrialModal(true);
        handleTrialComplete();
        return;
      }
      
      setLoggingDrink(drinkType);
      logDrinkMutation.mutate(drinkType);
    };

    // Calculate days info
    const daysRemaining = group ? calculateDaysRemaining(group.challenge_end) : 0;
    const isCompleted = group?.status === 'completed' || daysRemaining <= 0;
    const canLogDrinks = !isCompleted && !hasReachedLimit && loggingDrink === null;

    // Display code: use invite token
    const displayCode = inviteToken || '···';

    return (
      <section ref={ref} id="drink-log" className="min-h-[calc(100dvh-68px)] flex flex-col">
        {/* Welcome Message */}
        <div className="px-6 pt-4 text-center max-w-lg mx-auto w-full">
          <p className="text-sm text-muted-foreground">
            {t('dashboardHi')} {profile?.user_id}!
          </p>
          <p className="text-sm text-muted-foreground">
            {t('dashboardChallengeEnds')}{' '}
            <button
              onClick={() => inviteToken && setShowInviteModal(true)}
              className="font-bold font-mono underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
            >
              {displayCode}
            </button>{' '}
            {t('dashboardEndsIn')} <span className="font-bold">{daysRemaining}</span> {t('dashboardDays')}.
          </p>
          <button 
            type="button" 
            onClick={() => setShowInfoModal(true)}
            className="mt-2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full"
            aria-label="Drink standards info"
          >
            <Info className="h-8 w-8" />
          </button>
        </div>

        {/* Invite Modal */}
        {showInviteModal && inviteToken && (
          <InviteModal 
            token={inviteToken} 
            onClose={() => setShowInviteModal(false)} 
          />
        )}

        {/* Trial Modal */}
        <DrinkTrialModal 
          open={showTrialModal} 
          onClose={() => setShowTrialModal(false)} 
        />

        {/* Info Modal */}
        <DrinkInfoModal 
          open={showInfoModal} 
          onClose={() => setShowInfoModal(false)} 
        />

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-lg">
            {/* Stats Display */}
            <div className="text-center mb-8">
              <div className="text-6xl font-bold text-primary mb-2">
                {totalDrinks}
              </div>
              <p className="text-muted-foreground">
                {t('dashboardDrinksLogged')} · {remainingDrinks} {t('dashboardRemaining')}
              </p>
            </div>

            {/* Drink Buttons */}
            <div className="grid grid-cols-3 gap-4 justify-items-center mb-6">
              <DrinkButton icon="🍺" label={t('drinkBeer')} onDoubleTap={() => handleLogDrink('beer')} disabled={!canLogDrinks} />
              <DrinkButton icon="🍷" label={t('drinkWine')} onDoubleTap={() => handleLogDrink('wine')} disabled={!canLogDrinks} />
              <DrinkButton icon="🥃" label={t('drinkShot')} onDoubleTap={() => handleLogDrink('shot')} disabled={!canLogDrinks} />
            </div>

            {/* Guidance Tooltip or Instructions / Confirmation / Limit Reached */}
            <div className="min-h-[60px] flex items-start justify-center">
              {hasReachedLimit ? (
                <div className="bg-primary text-primary-foreground px-4 py-3 rounded-lg text-center shadow-lg w-full max-w-xs">
                  <p className="text-sm font-semibold">🏁 {t('dashboardReachedLimit')}</p>
                  <p className="text-sm">{t('dashboardWaitForEnd')}</p>
                </div>
              ) : showTooltip ? (
                <DrinkGuidanceTooltip step={currentStep} className="w-full max-w-xs" />
              ) : confirmationMessage ? (
                <span className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium animate-in fade-in duration-200">
                  {confirmationMessage}
                </span>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('dashboardDoubleTap')}
                </p>
              )}
            </div>


            {isCompleted && (
              <div className="mt-6 p-4 bg-card rounded-lg border border-border text-center">
                <p className="font-medium">{t('dashboardComplete')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('dashboardFinishedWith')} {totalDrinks} {t('dashboardDrinksLoggedFinal')}
                </p>
              </div>
            )}
          </div>
        </main>
      </section>
    );
  }
);
